import { z } from "zod";
import { calendarDateString } from "./calendar-date.js";
export const ChallengeMapStyle = z.enum(["calm", "street", "midnight", "satellite"]);
export const GetChallengePreferencesResponse = z.object({
    mapStyle: ChallengeMapStyle,
});
export const UpdateChallengePreferencesRequest = z.object({
    mapStyle: ChallengeMapStyle,
});
export const UpdateChallengePreferencesResponse = GetChallengePreferencesResponse;
export const RouteMilestone = z.object({
    fraction: z.number().min(0).max(1),
    title: z.string().min(1),
    description: z.string().min(1),
});
export const RoutePointSchema = z.object({ lat: z.number(), lng: z.number() });
export const JourneyGalleryImage = z.object({
    url: z.string().url(),
    alt: z.string().min(1),
    creator: z.string().min(1),
    license: z.string().min(1),
    sourceUrl: z.string().url(),
});
export const JourneyIntroduction = z.object({
    placeBlurb: z.string().min(1),
    gallery: z.array(JourneyGalleryImage).max(4),
});
export const JourneyCommunityMember = z.object({
    userId: z.string().uuid(),
    displayName: z.string().min(1),
    initials: z.string().min(1),
    avatarUrl: z.string().nullable(),
    avatarColor: z.string().nullable(),
    /** Unbounded lifetime progress. Values above 1 identify repeat route loops. */
    progressFraction: z.number().min(0),
});
export const JourneyCommunityPresence = z.object({
    memberCount: z.number().int().nonnegative(),
    members: z.array(JourneyCommunityMember).max(3),
    completedCount: z.number().int().nonnegative(),
    completedMembers: z.array(JourneyCommunityMember).max(3),
});
export const ChallengeCard = z.object({
    instanceId: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    target: z.number().int().positive(),
    unit: z.string(),
    artwork: z.unknown(),
    introduction: JourneyIntroduction.nullable(),
    imageUrl: z.string().url().nullable(),
    imageAlt: z.string().nullable(),
    imageAttribution: z
        .object({ creator: z.string(), license: z.string(), sourceUrl: z.string().url() })
        .partial(),
    routePath: z.array(z.object({ lat: z.number(), lng: z.number() })),
    routeMilestones: z.array(RouteMilestone),
    routeMode: z.enum(["WALKING", "BICYCLING"]),
    mapCenter: z.object({ lat: z.number(), lng: z.number() }).nullable(),
    mapZoom: z.number().int().nullable(),
    distanceMeters: z.number().int().positive().nullable(),
    community: JourneyCommunityPresence,
});
export const GetChallengeCatalogResponse = z.object({ challenges: z.array(ChallengeCard) });
/**
 * The consent wording a member agrees to when they join. Bump this when the
 * copy changes materially, so members are asked again rather than assumed in.
 */
export const WALKING_CHALLENGE_CONSENT_VERSION = "2026-08-27";
export const CompanyWalkingChallenge = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    description: z.string().min(1),
    startsOn: calendarDateString("startsOn"),
    endsOn: calendarDateString("endsOn"),
    targetSteps: z.number().int().positive(),
    targetMeters: z.number().int().positive(),
    progressSteps: z.number().int().nonnegative(),
    progressMeters: z.number().int().nonnegative(),
    /** False until the challenge's configured first calendar day. */
    hasStarted: z.boolean().optional(),
    participantCount: z.number().int().nonnegative(),
    /** Whether the caller has joined. Joining is consent to appear in standings. */
    viewerJoined: z.boolean(),
    /** The consent wording the caller agreed to, when they have joined. */
    viewerConsentVersion: z.string().nullable(),
    /** The one route selected by the company administrator for this challenge. */
    route: ChallengeCard,
});
export const CompanyChallengeRouteSelectionStrategy = z.enum(["fixed", "community_vote"]);
export const ScheduledCompanyWalkingChallenge = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    description: z.string().min(1),
    startsOn: calendarDateString("startsOn"),
    endsOn: calendarDateString("endsOn"),
    targetSteps: z.number().int().positive(),
    targetMeters: z.number().int().positive(),
    routeSelectionStrategy: CompanyChallengeRouteSelectionStrategy,
    route: ChallengeCard,
});
export const GetCompanyWalkingChallengeResponse = z.object({
    challenge: CompanyWalkingChallenge.nullable(),
});
export const UpdateCompanyWalkingChallengeRequest = z.object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().min(2).max(300),
    targetSteps: z.number().int().min(1_000).max(1_000_000_000),
    targetMeters: z.number().int().min(1_000).max(100_000_000),
    endsOn: calendarDateString("endsOn"),
    routeInstanceId: z.string().uuid(),
});
export const CreateCompanyWalkingChallengeRequest = UpdateCompanyWalkingChallengeRequest.extend({
    startsOn: calendarDateString("startsOn"),
});
export const CreateScheduledCompanyWalkingChallengeRequest = CreateCompanyWalkingChallengeRequest.extend({
    routeSelectionStrategy: CompanyChallengeRouteSelectionStrategy.default("fixed"),
});
export const UpdateScheduledCompanyWalkingChallengeRequest = CreateScheduledCompanyWalkingChallengeRequest;
/** A visible voter is deliberately limited to their existing community identity. */
export const CompanyChallengeRouteVoter = z.object({
    userId: z.string().uuid(),
    displayName: z.string().min(1),
    initials: z.string().min(1),
    avatarUrl: z.string().nullable(),
    avatarColor: z.string().nullable(),
});
export const CompanyChallengeRouteVoteOption = ChallengeCard.extend({
    voteCount: z.number().int().nonnegative(),
    voters: z.array(CompanyChallengeRouteVoter).max(3),
});
/** Null until the final seven calendar days of an active company challenge. */
export const CompanyChallengeRouteVote = z.object({
    challengeId: z.string().uuid(),
    endsOn: calendarDateString("endsOn"),
    viewerVotedRouteInstanceId: z.string().uuid().nullable(),
    totalVotes: z.number().int().nonnegative(),
    options: z.array(CompanyChallengeRouteVoteOption).min(1).max(3),
});
export const GetCompanyChallengeRouteVoteResponse = z.object({
    vote: CompanyChallengeRouteVote.nullable(),
});
/** Admin-only challenge workspace. Members never receive the route catalogue or vote totals here. */
export const GetAdminCompanyWalkingChallengeResponse = z.object({
    companyId: z.string().uuid(),
    companyName: z.string().nullable(),
    challenge: CompanyWalkingChallenge.nullable(),
    scheduledChallenge: ScheduledCompanyWalkingChallenge.nullable(),
    availableRoutes: z.array(ChallengeCard),
    nextRouteVote: CompanyChallengeRouteVote.nullable(),
});
export const CastCompanyChallengeRouteVoteRequest = z.object({
    routeInstanceId: z.string().uuid(),
});
export const RouteMilestoneSubject = z.enum(["member", "group"]);
/**
 * Waypoints and the finish share one celebration shape and one claim ledger, so
 * completion inherits once-only and the community share path for free. Copy is
 * the only thing that branches.
 */
export const RouteMilestoneKind = z.enum(["waypoint", "completion"]);
/**
 * Everything a route milestone celebration renders, on either platform and in
 * the community post it can become.
 *
 * `routePath` is a downsampled copy of the route rather than a reference to it.
 * A celebration, and any post made from one, is a snapshot of what was true at
 * the moment it happened; a later edit to the route must not rewrite it.
 */
export const RouteMilestoneCelebration = z.object({
    kind: RouteMilestoneKind,
    instanceId: z.string().uuid(),
    enrollmentId: z.string().uuid(),
    routeName: z.string().min(1),
    milestoneIndex: z.number().int().nonnegative(),
    milestoneTitle: z.string().min(1),
    milestoneDescription: z.string().min(1),
    milestoneFraction: z.number().min(0).max(1),
    /** Which waypoint this is, one-based, for copy like "waypoint 3 of 5". */
    milestoneOrdinal: z.number().int().positive(),
    milestoneCount: z.number().int().positive(),
    /** Unbounded pooled progress. Values above 1 identify repeat route loops. */
    progressFraction: z.number().min(0),
    distanceMeters: z.number().int().positive().nullable(),
    routePath: z.array(RoutePointSchema),
    routeMilestones: z.array(RouteMilestone),
    subject: RouteMilestoneSubject,
    /** Set for a group milestone; null when the member reached it alone. */
    groupId: z.string().uuid().nullable(),
    groupName: z.string().min(1).nullable(),
    memberCount: z.number().int().nonnegative().nullable(),
});
/**
 * A client asking to claim a milestone it believes it has crossed.
 *
 * The server re-derives the crossing from stored progress before it agrees, so
 * a client cannot mint a celebration, and the claim is idempotent so two
 * devices racing produce one moment.
 */
export const ClaimRouteMilestoneRequest = z.object({
    kind: RouteMilestoneKind.default("waypoint"),
    enrollmentId: z.string().uuid(),
    milestoneIndex: z.number().int().min(0).max(63),
    groupId: z.string().uuid().nullable().default(null),
});
export const ClaimRouteMilestoneResponse = z.object({
    /** False when this milestone was already claimed, or is not actually reached. */
    claimed: z.boolean(),
    celebration: RouteMilestoneCelebration.nullable(),
});
export const JoinCompanyWalkingChallengeRequest = z.object({
    consentVersion: z.string().min(1).max(40),
});
export const JoinCompanyWalkingChallengeResponse = z.object({
    challenge: CompanyWalkingChallenge.nullable(),
});
/** A rank crossing produced by the walking challenge ledger. */
export const CompanyChallengeOvertake = z.object({
    id: z.string().uuid(),
    challengeId: z.string().uuid(),
    routeInstanceId: z.string().uuid(),
    role: z.enum(["overtaker", "overtaken"]),
    otherMember: CompanyChallengeRouteVoter,
    overtakerSteps: z.number().int().nonnegative(),
    overtakenSteps: z.number().int().nonnegative(),
    occurredAt: z.string(),
});
export const ClaimCompanyChallengeOvertakesResponse = z.object({
    events: z.array(CompanyChallengeOvertake).max(8),
});
export const EnrollChallengeRequest = z.object({ instanceId: z.string().uuid() });
export const ChallengeEnrollmentParams = z.object({ enrollmentId: z.string().uuid() });
export const UpdateChallengeEnrollmentRequest = z.object({
    status: z.enum(["active", "paused", "abandoned"]),
});
/**
 * One person walking a route, as seen from another member's route map.
 *
 * `steps` is the route-eligible step total credited since enrollment.
 * `progressFraction` stays at 1 for a completed member, even when a final
 * step batch overshoots the target.
 * `distanceMeters` is the distance they covered over the same window.
 */
export const JourneyGroupProgressMember = z.object({
    id: z.string().uuid(),
    displayName: z.string().min(1),
    initials: z.string().min(1),
    avatarUrl: z.string().nullable(),
    avatarColor: z.string().nullable(),
    /** Unbounded while active; capped at 1 after completion. */
    progressFraction: z.number().min(0),
    steps: z.number().int().nonnegative(),
    distanceMeters: z.number().int().nonnegative(),
    groupIds: z.array(z.string().uuid()),
    groupNames: z.array(z.string().min(1)),
});
export const WalkingGroupRole = z.enum(["owner", "moderator", "member"]);
/**
 * A walking group's shared standing on one route.
 *
 * `progressFraction` is the group's pooled route-eligible steps over the route
 * target, the same denominator a member uses, so a group that has outwalked a
 * member stands ahead of them. One marker per group rather than a cloud of
 * member markers.
 */
export const JourneyRouteGroup = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    imageUrl: z.string().nullable(),
    memberCount: z.number().int().nonnegative(),
    /** Unbounded pooled progress. Values above 1 identify repeat route loops. */
    progressFraction: z.number().min(0),
    steps: z.number().int().nonnegative(),
    distanceMeters: z.number().int().nonnegative(),
    viewerRole: WalkingGroupRole.nullable(),
    members: z.array(JourneyGroupProgressMember),
});
export const GetJourneyGroupProgressResponse = z.object({
    /** Enrolled company peers on this route. Excludes the viewer. */
    members: z.array(JourneyGroupProgressMember),
    /** The viewer's own standing. Present whenever they are enrolled, with no
     *  floor on measured distance, so nobody is hidden from their own roster. */
    viewer: JourneyGroupProgressMember.optional(),
    /** The viewer's walking groups, each with its own route standing. Includes the viewer in `members`. */
    groups: z.array(JourneyRouteGroup),
    /** The route target, so a client can label a member total against it. */
    target: z.number().int().positive(),
    viewerUserId: z.string().uuid(),
});
/**
 * One calendar day of real route history: every enrolled member's and
 * group's cumulative `progressFraction` as of that date. Powers a scrubbable
 * playback of the route map from the earliest enrollment to today.
 */
export const JourneyProgressFrame = z.object({
    date: z.string(),
    memberProgress: z.record(z.string().uuid(), z.number().min(0)),
    groupProgress: z.record(z.string().uuid(), z.number().min(0)),
});
export const GetJourneyGroupProgressTimelineResponse = z.object({
    /** Ordered earliest to latest (today). Empty when nobody is enrolled yet. */
    frames: z.array(JourneyProgressFrame),
    target: z.number().int().positive(),
    viewerUserId: z.string().uuid(),
});
export const ChallengeEnrollment = z.object({
    enrollmentId: z.string().uuid(),
    instanceId: z.string().uuid(),
    status: z.string(),
    name: z.string(),
    target: z.number().int(),
    unit: z.string(),
    progress: z.number().int().nonnegative(),
    introduction: JourneyIntroduction.nullable(),
    imageUrl: z.string().url().nullable(),
    imageAlt: z.string().nullable(),
    imageAttribution: z
        .object({ creator: z.string(), license: z.string(), sourceUrl: z.string().url() })
        .partial(),
    routePath: z.array(z.object({ lat: z.number(), lng: z.number() })),
    routeMilestones: z.array(RouteMilestone),
    routeMode: z.enum(["WALKING", "BICYCLING"]),
    mapCenter: z.object({ lat: z.number(), lng: z.number() }).nullable(),
    mapZoom: z.number().int().nullable(),
    distanceMeters: z.number().int().positive().nullable(),
});
export const GetMyChallengesResponse = z.object({ challenges: z.array(ChallengeEnrollment) });
/** The complete member-facing Challenge workspace, assembled once on the server. */
export const ChallengeMilestoneReached = z.object({
    kind: z.enum(["waypoint", "completion"]),
    milestoneIndex: z.number().int().nonnegative(),
    reachedOn: calendarDateString("reachedOn"),
    /** First recorded step reading past the threshold, when available. */
    reachedAt: z.string().datetime().nullable(),
    timezone: z.string(),
});
export const GetChallengeWorkspaceResponse = z.object({
    /** The company-selected route, or null while the company has no active challenge. */
    route: ChallengeCard.nullable(),
    /** The member's progress on that one route, once they join the company challenge. */
    enrollment: ChallengeEnrollment.nullable(),
    milestoneHistory: z.array(ChallengeMilestoneReached).optional(),
});
export const ChallengeLeaderboardEntry = z.object({
    userId: z.string().uuid(),
    displayName: z.string().min(1),
    initials: z.string().min(1),
    avatarUrl: z.string().nullable(),
    avatarColor: z.string().nullable(),
    rank: z.number().int().positive(),
    score: z.number().int().nonnegative(),
    distanceMeters: z.number().int().nonnegative(),
});
export const ChallengeGroupLeaderboardEntry = z.object({
    groupId: z.string().uuid(),
    name: z.string().min(1),
    memberCount: z.number().int().nonnegative(),
    rank: z.number().int().positive(),
    score: z.number().int().nonnegative(),
    distanceMeters: z.number().int().nonnegative(),
});
export const ChallengeLeaderboardPeriodKind = z.enum([
    "week",
    "weekend",
    "month",
    "route",
    "final",
]);
export const ChallengeLeaderboard = z.object({
    instanceId: z.string().uuid(),
    title: z.string().min(1),
    period: z.string().min(1),
    periodKind: ChallengeLeaderboardPeriodKind,
    scope: z.enum(["company", "route"]),
    participantCount: z.number().int().nonnegative(),
    totalScore: z.number().int().nonnegative(),
    averageScore: z.number().int().nonnegative(),
    entries: z.array(ChallengeLeaderboardEntry),
    myEntry: ChallengeLeaderboardEntry.nullable(),
    /**
     * The person the viewer is racing, counted from the full board. First place
     * is ahead of the person behind them. Everyone else is behind the person
     * one place above. Null when they are unranked or alone. Optional so a
     * board built before this field still parses.
     */
    nearestRival: z
        .object({
        direction: z.enum(["ahead", "behind"]),
        steps: z.number().int().nonnegative(),
        displayName: z.string().min(1),
        rank: z.number().int().positive(),
        initials: z.string().min(1).nullable().optional(),
        avatarUrl: z.string().nullable().optional(),
        avatarColor: z.string().nullable().optional(),
    })
        .nullable()
        .optional(),
    hasMore: z.boolean(),
    groupEntries: z.array(ChallengeGroupLeaderboardEntry),
});
/**
 * An additional standing for members whose daily step target sits in the same
 * band. It does not replace the company or route boards: every participant
 * remains eligible for those broader standings too.
 */
export const ChallengeTargetLeaderboard = z.object({
    /**
     * The band's representative target, one of the three presets. A member who
     * typed a custom number is counted in the band it falls in rather than in a
     * standing of one, so this is not necessarily the number they chose. Name it
     * with `stepTargetBandLabel` from `@healthos/shared`.
     */
    targetSteps: z.number().int().positive(),
    participantCount: z.number().int().nonnegative(),
    entries: z.array(ChallengeLeaderboardEntry),
    myEntry: ChallengeLeaderboardEntry.nullable(),
    hasMore: z.boolean(),
});
/**
 * Someone worth noticing who a steps leaderboard would not surface.
 *
 * A ranking by volume mostly ranks whose commute is longest. These name the
 * member who turned up most often and the member who had the single biggest
 * day, so a company sees more than the same five names every week.
 */
export const ChallengeRecognitionKind = z.enum(["most_consistent", "longest_streak", "best_day"]);
export const ChallengeRecognition = z.object({
    kind: ChallengeRecognitionKind,
    userId: z.string().uuid(),
    displayName: z.string().min(1),
    initials: z.string().min(1),
    avatarUrl: z.string().nullable(),
    avatarColor: z.string().nullable(),
    /** Days on target for `most_consistent`, days in the run for
     *  `longest_streak`, steps for `best_day`. */
    value: z.number().int().nonnegative(),
    /** The day it happened, for `best_day`. Null otherwise. */
    occurredOn: calendarDateString("occurredOn").nullable(),
});
export const GetChallengeLeaderboardsResponse = z.object({
    /** False before the current company challenge's first calendar day. */
    leaderboardsAvailable: z.boolean().optional(),
    leaderboards: z.array(ChallengeLeaderboard),
    /** Extra route standings, grouped by each member's current daily step target. */
    targetLeaderboards: z.array(ChallengeTargetLeaderboard),
    /**
     * Empty when nobody qualifies yet, so a surface omits the section rather
     * than rendering a card with nothing in it.
     */
    recognition: z.array(ChallengeRecognition),
    /**
     * The board a surface opens on. Company standings when the company runs one,
     * otherwise its route standings, which are a real standing and not a fallback
     * to apologise for. Decided here because both clients used to decide it, and
     * disagreed: web preferred a company board and fell back to the route, mobile
     * took whichever board happened to be first.
     *
     * Null only when there are no boards at all.
     */
    primaryInstanceId: z.string().uuid().nullable(),
});
export const ChallengeLeaderboardParams = z.object({ instanceId: z.string().uuid() });
export const ChallengeLeaderboardPageQuery = z.object({
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(10),
});
export const GetChallengeLeaderboardPageResponse = z.object({
    entries: z.array(ChallengeLeaderboardEntry),
    totalCount: z.number().int().nonnegative(),
    hasMore: z.boolean(),
});
export const ChallengeLeaderboardGroupParams = z.object({
    instanceId: z.string().uuid(),
    groupId: z.string().uuid(),
});
/** The members behind one group row on a leaderboard, ranked inside the group. */
export const GetChallengeLeaderboardGroupResponse = z.object({
    groupId: z.string().uuid(),
    name: z.string().min(1),
    entries: z.array(ChallengeLeaderboardEntry),
});
/**
 * A company walking challenge whose period has ended, as the member who took
 * part in it sees it in their history. "Past" means the challenge window is
 * over; whether the member finished the route is a stat, not the gate.
 */
export const PastCompanyChallengeSummary = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    description: z.string().min(1),
    startsOn: calendarDateString("startsOn"),
    endsOn: calendarDateString("endsOn"),
    participantCount: z.number().int().nonnegative(),
    /** Up to five consenting participants for the card's avatar group. */
    participantsPreview: z.array(CompanyChallengeRouteVoter).max(5),
    routeName: z.string().min(1),
    /**
     * Downsampled copy of the route for the card map, following the
     * RouteMilestoneCelebration precedent: a snapshot, not a reference.
     */
    routePath: z.array(RoutePointSchema),
    routeMode: z.enum(["WALKING", "BICYCLING"]),
    mapCenter: z.object({ lat: z.number(), lng: z.number() }).nullable(),
    mapZoom: z.number().int().nullable(),
    distanceMeters: z.number().int().positive().nullable(),
    /** The member's own outcome, for the card footer. */
    viewer: z.object({
        steps: z.number().int().nonnegative(),
        distanceMeters: z.number().int().nonnegative(),
        /** Route progress over the route target. At or above 1 means they finished. */
        progressFraction: z.number().min(0),
        finishedRoute: z.boolean(),
        /** Final rank on the company standings, or null when they logged no steps. */
        rank: z.number().int().positive().nullable(),
        /**
         * How many steps separated them from the member one place above, for
         * "16,005 steps behind 3rd place". Null in first place or unranked.
         */
        stepsBehindNext: z.number().int().nonnegative().nullable(),
        /** Longest run of days on their step target inside the challenge window. */
        longestStreakDays: z.number().int().nonnegative(),
        bestDaySteps: z.number().int().nonnegative(),
        bestDayOn: calendarDateString("bestDayOn").nullable(),
        /** Days with any route-eligible contribution inside the window. */
        activeDays: z.number().int().nonnegative(),
    }),
    /** The member's own display identity, for their row on the overview card. */
    viewerIdentity: CompanyChallengeRouteVoter.nullable(),
    /** The final podium: the top three of the company standings. */
    topThree: z.array(ChallengeLeaderboardEntry).max(3),
});
export const GetChallengeHistoryResponse = z.object({
    challenges: z.array(PastCompanyChallengeSummary),
});
export const PastChallengeParams = z.object({ challengeId: z.string().uuid() });
/**
 * Everything user-specific about one past challenge, condensed for the
 * personal overview card. The contract carries the full set so later
 * iterations can widen the card without another contract change.
 */
export const PastChallengeViewerStats = z.object({
    steps: z.number().int().nonnegative(),
    distanceMeters: z.number().int().nonnegative(),
    rank: z.number().int().positive().nullable(),
    progressFraction: z.number().min(0),
    finishedRoute: z.boolean(),
    /** Longest run of days on their step target inside the challenge window. */
    longestStreakDays: z.number().int().nonnegative(),
    bestDaySteps: z.number().int().nonnegative(),
    bestDayOn: calendarDateString("bestDayOn").nullable(),
    /** Days with any route-eligible contribution inside the window. */
    activeDays: z.number().int().nonnegative(),
    milestonesReached: z.number().int().nonnegative(),
});
export const PastCompanyChallengeStats = z.object({
    totalSteps: z.number().int().nonnegative(),
    totalMeters: z.number().int().nonnegative(),
    targetSteps: z.number().int().positive(),
    targetMeters: z.number().int().positive(),
    participantCount: z.number().int().nonnegative(),
    routeFinisherCount: z.number().int().nonnegative(),
    durationDays: z.number().int().positive(),
});
/**
 * The whole past-challenge detail, assembled once on the server the way the
 * live workspace is. Standings are computed over the challenge window from
 * the same ledgers the live boards read; nothing here is stored anew.
 */
export const GetPastCompanyChallengeResponse = z.object({
    challenge: PastCompanyChallengeSummary,
    /** The full route for the map, reusing the live shape end to end. */
    route: ChallengeCard,
    /**
     * The member's enrollment on the route, for the timeline replay endpoint
     * (`GET /enrollments/:id/group-progress-timeline`). Null when they joined
     * the challenge but never enrolled on its route.
     */
    viewerEnrollmentId: z.string().uuid().nullable(),
    /** Final standings: the company board (periodKind "final") and the route board. */
    leaderboards: z.array(ChallengeLeaderboard),
    stats: PastCompanyChallengeStats,
    viewerStats: PastChallengeViewerStats,
    /**
     * Recognition callouts computed over the whole challenge window and its
     * participants, not the live seven-day company-wide version.
     */
    recognition: z.array(ChallengeRecognition),
});
/** Another member, and how many times something happened between the two of you. */
export const ChallengeWrapUpRival = z.object({
    member: CompanyChallengeRouteVoter,
    count: z.number().int().positive(),
});
/**
 * The facts the challenge wrap-up needs that the past-challenge detail does
 * not already carry: who the member passed, who passed them, and who finished
 * nearest. Everything else on the wrap-up (steps, medals, streaks, rank) is
 * derived on the client from `GetPastCompanyChallengeResponse`.
 */
export const GetChallengeWrapUpResponse = z.object({
    overtakes: z.object({
        /** Rank crossings where the member moved past someone. */
        made: z.number().int().nonnegative(),
        /** Rank crossings where someone moved past the member. */
        received: z.number().int().nonnegative(),
        /** How many different members the member passed at least once. */
        distinctPassed: z.number().int().nonnegative(),
        /** The member they passed most often, or null with no passes. */
        mostPassed: ChallengeWrapUpRival.nullable(),
        /** The member who passed them most often, or null when nobody did. */
        mostPassedBy: ChallengeWrapUpRival.nullable(),
    }),
    /**
     * The neighbour on the final standings with the smallest step gap, above or
     * below. Null when the member is unranked or alone on the board.
     */
    closestRival: z
        .object({
        member: CompanyChallengeRouteVoter,
        stepsApart: z.number().int().nonnegative(),
        /** Where the rival finished relative to the member. */
        position: z.enum(["ahead", "behind"]),
    })
        .nullable(),
});
export const CreateWalkingGroupRequest = z.object({
    name: z.string().trim().min(2).max(80),
    description: z.string().trim().max(240).nullable().default(null),
    visibility: z.enum(["open", "invite_only"]).default("open"),
});
export const WalkingGroup = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    visibility: z.enum(["open", "invite_only"]),
    role: z.string().nullable().optional(),
    memberCount: z.number().int().nonnegative(),
    invitationId: z.string().uuid().nullable(),
    /** Auth-gated delivery path for the group photo, or null when unset. */
    imageUrl: z.string().nullable(),
});
export const GetWalkingGroupsResponse = z.object({ groups: z.array(WalkingGroup) });
/**
 * One member's movement inside a walking group. Totals are measured from the
 * day the member joined the group, so a newcomer's number is small and honest
 * rather than backfilled from before they were part of it.
 */
export const WalkingGroupMember = z.object({
    userId: z.string().uuid(),
    displayName: z.string().min(1),
    initials: z.string().min(1),
    avatarUrl: z.string().nullable(),
    avatarColor: z.string().nullable(),
    role: WalkingGroupRole,
    joinedOn: calendarDateString("joinedOn"),
    steps: z.number().int().nonnegative(),
    distanceMeters: z.number().int().nonnegative(),
});
export const GetWalkingGroupMembersResponse = z.object({
    group: WalkingGroup,
    totals: z.object({
        steps: z.number().int().nonnegative(),
        distanceMeters: z.number().int().nonnegative(),
    }),
    members: z.array(WalkingGroupMember),
});
/**
 * Group photo upload. Same contract as the member profile image: the client
 * sends cropped base64 bytes, the server re-validates, strips EXIF, re-encodes
 * to JPEG, and stores the derivative as a published `community_assets` row.
 * Only the group owner may call it.
 */
export const UploadWalkingGroupImageRequest = z.object({
    dataBase64: z.string().min(1),
    mime: z.enum(["image/jpeg", "image/png", "image/webp"]),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
});
export const UploadWalkingGroupImageResponse = z.object({ group: WalkingGroup });
export const RemoveWalkingGroupImageResponse = z.object({ removed: z.boolean() });
export const DistanceGoalScope = z.enum(["personal", "group"]);
export const DistanceGoal = z.object({
    id: z.string().uuid(),
    scope: DistanceGoalScope,
    name: z.string().min(1),
    targetMeters: z.number().int().positive(),
    progressMeters: z.number().int().nonnegative(),
    progressFraction: z.number().min(0).max(1),
    startsOn: z.string(),
    groupId: z.string().uuid().nullable(),
    groupName: z.string().nullable(),
    challengeInstanceId: z.string().uuid().nullable(),
});
export const GetDistanceGoalsResponse = z.object({ goals: z.array(DistanceGoal) });
export const CreateDistanceGoalRequest = z
    .object({
    scope: DistanceGoalScope,
    name: z.string().trim().min(2).max(100),
    targetMeters: z.number().int().min(500).max(100_000_000),
    groupId: z.string().uuid().nullable().optional(),
    challengeInstanceId: z.string().uuid().nullable().optional(),
})
    .superRefine((value, context) => {
    if (value.scope === "group" && !value.groupId) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "Choose a group for this goal." });
    }
    if (value.scope === "personal" && value.groupId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Personal goals cannot belong to a group.",
        });
    }
});
export const WalkingGroupParams = z.object({ groupId: z.string().uuid() });
export const WalkingGroupInvitationParams = z.object({ invitationId: z.string().uuid() });
export const CreateWalkingGroupInvitationRequest = z.object({ userId: z.string().uuid() });
export const RespondWalkingGroupInvitationRequest = z.object({
    response: z.enum(["accepted", "declined"]),
});
/**
 * How many people one Teams people-picker nudge may reach. Mirrors the
 * in-app picker's own MAX_INVITEES so the two entry points in the same
 * dialog cap at the same number. Note there is no SDK-side cap to inherit:
 * `people.selectPeople`'s PeoplePickerInputs (teams-js 2.55) offers
 * `singleSelect` and nothing else, so multi-select is unbounded at the host
 * and this is the only limit.
 */
export const MAX_TEAMS_BOT_INVITEES = 10;
/**
 * Entra object ids straight off `people.selectPeople`. No tenant id: the
 * server resolves that from the inviter's own linked Teams identity, so a
 * client cannot aim this at another tenant's conversation store.
 */
export const CreateTeamsBotWalkingGroupInvitationsRequest = z.object({
    aadObjectIds: z.array(z.string().uuid()).min(1).max(MAX_TEAMS_BOT_INVITEES),
});
/**
 * How many cards actually went out. Deliberately not a per-person
 * reachability breakdown: an invitee who has never used the bot is not a
 * delivery failure the inviter gets told about.
 */
export const CreateTeamsBotWalkingGroupInvitationsResponse = z.object({
    sent: z.number().int().nonnegative(),
});
//# sourceMappingURL=challenges.js.map